import { FeatureFlagKey, type FeatureFlagMap } from "encamp-shared/src/featureFlags";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { type PropsWithChildren, type ReactElement } from "react";
import { Navigate } from "react-router-dom";

export const SCOUT_ACCESS_PACKAGES = {
  REGULATORY_ADVISOR: "regulatory-advisor",
  PRO: "pro",
} as const;

export type ScoutAccessPackage = (typeof SCOUT_ACCESS_PACKAGES)[keyof typeof SCOUT_ACCESS_PACKAGES];

export type ScoutAccessInput = {
  flags: Partial<FeatureFlagMap> | undefined;
  hasPermissions: (permissions: Permission[]) => boolean;
  isStaff?: boolean;
};

export function resolveScoutAccessPackage({
  flags,
  hasPermissions,
  isStaff = false,
}: ScoutAccessInput): ScoutAccessPackage | null {
  if (!flags?.[FeatureFlagKey.ScoutModule]) {
    return null;
  }

  if (isStaff) {
    return SCOUT_ACCESS_PACKAGES.PRO;
  }

  if (flags[FeatureFlagKey.ScoutProModule]) {
    const proIsScoped = Boolean(flags[FeatureFlagKey.ScoutProPermissionScoped]);
    if (!proIsScoped || hasPermissions([Permission.AccessScoutPro])) {
      return SCOUT_ACCESS_PACKAGES.PRO;
    }
  }

  const regulatoryAdvisorIsScoped = Boolean(
    flags[FeatureFlagKey.ScoutRegulatoryAdvisorPermissionScoped]
  );
  if (regulatoryAdvisorIsScoped && !hasPermissions([Permission.RouteScout])) {
    return null;
  }

  return SCOUT_ACCESS_PACKAGES.REGULATORY_ADVISOR;
}

export function hasScoutAccess(input: ScoutAccessInput): boolean {
  return resolveScoutAccessPackage(input) !== null;
}

export function useScoutAccessPackage(): {
  scoutAccessPackage: ScoutAccessPackage | null;
  loading: boolean;
} {
  const { featureFlags, loading: featureFlagsLoading } = useFeatureFlags();
  const { hasPermissions, loading: authorizationLoading } = useAuthorization();
  const { isStaff, isEmulating, loading: currentUserLoading } = useCurrentUser();

  return {
    scoutAccessPackage: resolveScoutAccessPackage({
      flags: featureFlags,
      hasPermissions,
      isStaff: isStaff && !isEmulating,
    }),
    loading: featureFlagsLoading || authorizationLoading || currentUserLoading,
  };
}

export function HasScoutAccess({
  fallback,
  children,
}: PropsWithChildren<{
  fallback?: string | ReactElement;
}>) {
  const { scoutAccessPackage, loading } = useScoutAccessPackage();

  if (loading) {
    return <></>;
  }

  if (scoutAccessPackage) {
    return <>{children}</>;
  }

  if (fallback && typeof fallback !== "string") {
    return fallback;
  }

  return <Navigate to={fallback ?? "../not-found"} replace />;
}
